import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DataResult } from '@progress/kendo-data-query';
import { ComboBoxHandle } from '@progress/kendo-react-dropdowns';
import { GridColumnState } from '@progress/kendo-react-grid';
import { CardHandle } from '@progress/kendo-react-layout';
import cn from 'clsx';
import styles from './style.module.scss';
import './style.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import BulkUpdateIcon from '@components-clean/core/grid/bulk-update-icon';
import ClearFiltersIcon from '@components-clean/core/grid/clear-filters-icon';
import GridMenu from '@components-clean/core/grid/grid-menu';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import GridSettingsMenu from '@components-clean/core/grid/grid-menu/grid-settings-menu';
import GridStateMenu from '@components-clean/core/grid/grid-menu/grid-state-menu';
import GridViewMenu from '@components-clean/core/grid/grid-menu/grid-view-menu';
import GridStatusBar from '@components-clean/core/grid/grid-status';
import { GridColumnProps, GridHeaderTitle, GridProps, GridState } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid/index';
import NumberOfRecords from '@components-clean/core/grid/number-of-records';
import { GRID_COMPONENT_NAME } from '@components-clean/core/grid/shared';
import { updateColumnsFields } from '@components-clean/core/grid/update-columns-fields';
import { UnfilterableIcon } from '@components-clean/core/icons/unfilterable-icon/unfilterable-icon';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';

interface FormValues {
    selectedHeader: GridHeaderTitle;
}

export type CoreGridHandle = {
    unselectRow: (primaryKey: string) => void;
    validateSelection: (newData: unknown[]) => void;
};

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const CardGrid = forwardRef<CoreGridHandle, GridProps>((props: GridProps, ref) => {
    const { t } = useTranslation();

    const saveTableStateRef = useRef(false);

    const [dataState, setDataState] = React.useState<GridState>({
        data: [],
        total: Array.isArray(props.data) ? props.data.length : props.data ? props.data.data.length : 0,
        filter: props.filter,
        sort: props.sort ?? [],
    });

    const cardRef = useRef<CardHandle>(null);

    const {
        confirmDraggableChangesWillClearFilters,
        confirmDiscardChanges,
        setComponentHasChanges,
        componentHasChanges,
        anyComponentHasChanges,
        anyCentralMonitoringToolkitHasChanges,
        anySubjectMonitoringToolkitHasChanges,
        anyRiskAssessmentToolkitHasChanges,
    } = useUnsavedChangesContext();

    const filterActive = useBoolean(false);
    const showAddWarningDialog = useBoolean(false);
    const lockedColumnsUpdated = useBoolean(false);
    const reorderActive = useBoolean(false);
    const awaitData = useBoolean(false);
    const hiddenColumnsUpdated = useBoolean(false);

    // Use gridKey/setGridKey if provided, otherwise use internal state
    const [internalGridKey, setInternalGridKey] = useState(0);
    const gridKey = props.gridKey !== undefined ? props.gridKey : internalGridKey;
    const setGridKey = props.setGridKey !== undefined ? props.setGridKey : setInternalGridKey;

    const [tmpReorder, setTmpReorder] = useState<unknown[] | DataResult | null>();
    const [selectedState, setSelectedState] = useState<{
        [id: string]: boolean | number[];
    }>({});
    const [currentColumns, setCurrentColumns] = useState<GridColumnProps[]>(() =>
        updateColumnsFields(props.columns, props.rowReorder, props.orderIndexColumn, []),
    );

    const [columnsStateToApply, setColumnsStateToApply] = useState<GridColumnState[] | undefined>(undefined);
    const [gridStateToken, setGridStateToken] = useState(0);
    const [currentGridStateJson, setCurrentGridStateJson] = useState('');

    const [selectedHeader, setSelectedHeader] = useState<GridHeaderTitle | null | undefined>(props.selectedTitle);

    const titleIsEmpty = Array.isArray(props.title) && !selectedHeader;
    let cardTitle: GridProps['title'] | ReactNode = props.title;

    const chips =
        Array.isArray(props.title) && props.onChipHeaderSelected
            ? props.title.map((x) => ({
                  text: x.label as string,
                  value: x.id,
              }))
            : [];

    // Cases where chip isn't set don't set it to 1
    const [chip, setChip] = useState<number | undefined>(props.selectedChipHeader);
    const { control, watch } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: {
            selectedHeader: props.selectedTitle ?? undefined,
        },
    });
    const headerDropdownValue = watch('selectedHeader');

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (headerDropdownValue) setSelectedHeader(headerDropdownValue);
        if (props.onTitleChange) {
            props.onTitleChange({
                value: headerDropdownValue,
                nativeEvent: {} as Event,
                syntheticEvent: {} as React.SyntheticEvent<HTMLElement, Event>,
                target: {} as ComboBoxHandle,
            });
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [headerDropdownValue]);

    useEffect(() => {
        if (chips.length > 0) {
            const header: GridHeaderTitle = {
                label: chips[0].text,
                id: chips[0].value,
            };
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setSelectedHeader(header);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedHeader(props.selectedTitle);
    }, [props.selectedTitle]);

    useEffect(() => {
        if (awaitData.value) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setTmpReorder(props.data);
            awaitData.setFalse();
            reorderActive.setTrue();
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [props.data]);

    const updateChip = (tabIndex: number) => {
        if (tabIndex !== null) {
            if (props.onChipHeaderSelected) {
                props.onChipHeaderSelected(tabIndex);
                const selectedChip = chips.find((item) => item.value === tabIndex);
                const header: GridHeaderTitle = {
                    label: selectedChip?.text as string,
                    id: selectedChip?.value as string,
                };
                setSelectedHeader(header);
            }
            setChip(tabIndex);
        }
    };

    // Confirmation dialog setup for enabling the toggle reorder. If the user informs us of a data change pending,
    // then we will wait for that change before updating our state with the awaitData state.
    const confirmationSubmit = () => {
        setTmpReorder(props.data);
        awaitData.setTrue();
        const dataChanges = props.toggleReorder && props.toggleReorder(true);
        if (!dataChanges) {
            awaitData.setFalse();
            reorderActive.setTrue();
            props.setReorderIsActive && props.setReorderIsActive();
        }
    };

    if (Array.isArray(props.title)) {
        if (props.onChipHeaderSelected) {
            cardTitle = (
                <ChipList
                    // Compound key forces the ChipList to remount whenever any tracked dirty-state flips.
                    // Without this, Kendo ChipList's internal focus state can steal focus from input
                    // elements (e.g. textareas inside open dialogs) on subsequent re-renders.
                    // Same workaround as @views-clean/pages/admin/study/admin-data-views/filters.
                    /* eslint-disable-next-line max-len */
                    key={`${anyComponentHasChanges()}-${anyCentralMonitoringToolkitHasChanges()}-${anySubjectMonitoringToolkitHasChanges()}-${anyRiskAssessmentToolkitHasChanges()}`}
                    className={cn(props.isChildGrid ? 'plum' : '')}
                    data-testid="filters-chip-list"
                    data={chips}
                    value={chip}
                    onChange={(event) => {
                        updateChip(event.value);
                    }}
                    selection="single"
                />
            );
        } else {
            cardTitle = (
                <div className={styles.minHeaderWidth} style={{ width: props.titleWidth }}>
                    <BasicDropDownController
                        data-testid="table-header-selector"
                        data={props.title}
                        dataItemKey="id"
                        control={control}
                        textField="label"
                        readOnly={reorderActive.value || props.titleReadOnly}
                        type="card-header"
                        activeField="active"
                        groupField={props.titleGroupByField}
                        name="selectedHeader"
                        className="!k-basis-1/2"
                        cardHeader
                        filterable
                    />
                </div>
            );
        }
    }

    const clearFilterAndSortOnLayoutSwitch = () => {
        props.clearFilter?.()?.catch(() => {});
        setDataState((prevState) => ({
            ...prevState,
            filter: { logic: 'and', filters: [] },
            sort: [],
        }));
    };

    const clearFilterAndRerender = async () => {
        if (props.clearFilter) {
            const proceed = await props.clearFilter();
            if (proceed === false) return;
        }
        if (props.persistentStorageKey) {
            setDataState((prevState) => ({
                ...prevState,
                filter: { logic: 'and', filters: [] },
            }));
        }

        setGridKey((prev) => prev + 1);
    };

    const gridMenuItems: GridMenuItemProps[] = [
        ...(props.customGridMenuItems ?? []),
        ...(props.filterable || props.filterable === undefined
            ? [
                  {
                      title: `${t('clearFilters')}`,
                      'data-testid': 'grid-clear-filters',
                      customIcon: <ClearFiltersIcon appliedFilters={props.filter?.filters.length ?? 0} />,
                      onClick: clearFilterAndRerender,
                      disabled: titleIsEmpty || !props.filter?.filters.length,
                      ignoreReadOnlyContext: true,
                  },
              ]
            : []),
        ...(props.showBulkUpdateIcon
            ? [
                  {
                      'data-testid': 'grid-bulk-update',
                      customIcon: <BulkUpdateIcon tooltip={props.bulkUpdateTooltip} />,
                      title: `${t('bulkUpdate')}`,
                      disabled:
                          !props.canEdit ||
                          Object.keys(selectedState).filter((k) => selectedState[k] === true).length <= 1,
                      onClick: props.onBulkUpdateClicked,
                  },
              ]
            : []),
        ...(props.showSendEmailIcon
            ? [
                  {
                      'data-testid': 'grid-send-email',
                      title: `${t('sendEmail')}`,
                      icon: ['far', 'envelope'] as IconProp,
                      disabled:
                          !props.canEdit ||
                          !Object.keys(selectedState).length ||
                          !Object.keys(selectedState).find((k) => selectedState[k] === true),
                      onClick: () => {
                          props.emailCallback && props.emailCallback(selectedState);
                      },
                  },
              ]
            : []),
        ...(props.gridViewOptions
            ? [
                  {
                      title: `${t('layout')}`,
                      icon: ['far', 'table-layout'] as IconProp,
                      'data-testid': 'grid-view-options',
                      children: <GridViewMenu viewOptions={props.gridViewOptions} />,
                      disabled: titleIsEmpty,
                  },
              ]
            : []),
        ...(props.rowReorder
            ? [
                  {
                      title: `${t('reorderMode')}`,
                      customIcon: (
                          <span
                              title={t('reorderMode')}
                              className="k-icon k-font-icon k-i-replace-single"
                              style={{ fontSize: '18px', verticalAlign: 'baseline' }}
                          />
                      ),
                      'data-testid': 'grid-reorder-mode',
                      onClick: async () => {
                          if (props.reorderConfirmation) {
                              await props.reorderConfirmation(confirmationSubmit, () => {});
                          } else {
                              const confirmed = await confirmDraggableChangesWillClearFilters(
                                  '',
                                  t('common:gridReorderConfirmation'),
                                  'reorder-confirmation-dialog',
                              );
                              if (confirmed) {
                                  confirmationSubmit();
                              }
                          }
                      },
                      disabled: !props.rowReorder || !props.canEdit || titleIsEmpty,
                  },
              ]
            : []),
        ...(props.apiGridMenuActionItems ? props.apiGridMenuActionItems : []),
        ...(!props.gridDownloadDisabled
            ? [
                  {
                      title: `${t('excelExport')}`,
                      icon: ['far', 'file-excel'] as IconProp,
                      'data-testid': 'grid-excel-export',
                      onClick: props.exportCallback,
                      disabled: titleIsEmpty || props.isExporting,
                  },
              ]
            : []),
        ...(props.gridRefresh
            ? [
                  {
                      'data-testid': 'refresh-grid-data',
                      title: t('common:grid.refresh'),
                      icon: ['far', 'refresh'] as IconProp,
                      onClick: () => props.gridRefresh!(),
                  },
              ]
            : []),
        ...(!props.gridSettingsMenuDisabled
            ? [
                  {
                      title: `${t('settings')}`,
                      icon: ['far', 'gear'] as IconProp,
                      'data-testid': 'grid-settings-menu',
                      children: (
                          <GridSettingsMenu
                              columns={currentColumns}
                              updateColumns={setCurrentColumns}
                              lockedColumnsUpdated={lockedColumnsUpdated}
                              hiddenColumnsUpdated={hiddenColumnsUpdated}
                              integrationsOptions={props.integrationsOptions}
                          />
                      ),
                      disabled: titleIsEmpty,
                  },
              ]
            : []),
        ...(props.showAddIcon || props.showAddIcon === undefined
            ? [
                  {
                      title: `${props.addRecordTooltip || t('addRecord')}`,
                      icon: ['far', 'plus'] as IconProp,
                      'data-testid': 'grid-add-record',
                      onClick:
                          !props.suppressAddFilterClearWarning && filterActive.value
                              ? showAddWarningDialog.setTrue
                              : props.addCallback,
                      disabled: titleIsEmpty || !props.canEdit,
                  },
              ]
            : []),
    ];

    const handleGridReorderCancel = () => {
        if (!props.cancelReorder || props.cancelReorder()) {
            setTmpReorder(undefined);
            props.toggleReorder && props.toggleReorder(false);
            reorderActive.setFalse();
        }
    };

    return (
        <Card
            ref={cardRef}
            title={
                <div className="k-d-flex k-align-items-center">
                    <div>{cardTitle as string | ReactNode}</div>
                    <div className="k-ml-3">
                        <NumberOfRecords
                            number={(props.totalCount as number) ?? (Array.isArray(props.data) ? props.data.length : 0)}
                        />
                    </div>
                    {props.screenFilterable === false && (
                        <div className="k-ml-3">
                            <UnfilterableIcon
                                dataTestId={`${props.testDataId}-unfilterable-icon`}
                                className={styles.nonScreenFilterable}
                                tooltip={t('grid.unaffectedByScreenFilters')}
                            />
                        </div>
                    )}
                </div>
            }
            data-testid={props.testDataId}
            style={{ maxHeight: props.maxHeight, height: '100%', width: '100%' }}
            padding={props.noPadding ? 'none' : undefined}
            headerPadding={Array.isArray(props.title) ? 'small' : 'default'}
            className={cn('k-h-full', props.removeCardBorder ? 'remove-grid-border' : '')}
            headerInfo={
                props.gridStatusSetup ? (
                    <GridStatusBar
                        integrationStatusSetup={props.gridStatusSetup.integrationStatusSetup}
                        automationStatusSetup={props.gridStatusSetup.automationStatusSetup}
                    />
                ) : undefined
            }
            headerActions={
                reorderActive.value ? (
                    <>
                        <Button
                            data-testid="reorder-cancel"
                            onClick={async () => {
                                if (componentHasChanges(GRID_COMPONENT_NAME)) {
                                    const confirmedDiscard = await confirmDiscardChanges(
                                        'data-view-config-cancel-confirmation',
                                    );
                                    if (!confirmedDiscard) {
                                        return;
                                    }
                                    setComponentHasChanges(GRID_COMPONENT_NAME, false);
                                }
                                handleGridReorderCancel();
                            }}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            data-testid="reorder-save"
                            primary
                            onClick={() => {
                                if (!props.saveReorder || props.saveReorder(tmpReorder)) {
                                    setTmpReorder(undefined);
                                    props.toggleReorder && props.toggleReorder(false);
                                    reorderActive.setFalse();
                                }
                            }}
                        >
                            {t('save')}
                        </Button>
                    </>
                ) : (
                    <div className="k-d-flex">
                        {props.persistentStorageKey && (
                            <GridStateMenu
                                gridId={Number(props.persistentStorageKey)}
                                currentGridState={currentGridStateJson}
                                gridStateVersion={String(gridStateToken)}
                                onApplyColumnsState={setColumnsStateToApply}
                                onLayoutChange={clearFilterAndSortOnLayoutSwitch}
                                onSettingApplied={setCurrentGridStateJson}
                            />
                        )}
                        <GridMenu items={gridMenuItems} />
                        {!!props.bespokeHeaderActions && props.bespokeHeaderActions}
                    </div>
                )
            }
        >
            <Grid
                {...props}
                ref={ref}
                cardRef={cardRef}
                titleIsEmpty={titleIsEmpty}
                filterActive={filterActive}
                showAddWarningDialog={showAddWarningDialog}
                lockedColumnsUpdated={lockedColumnsUpdated}
                reorderActive={reorderActive}
                gridKey={gridKey}
                setGridKey={setGridKey}
                tmpReorder={tmpReorder}
                setTmpReorder={setTmpReorder}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                currentColumns={currentColumns}
                setCurrentColumns={setCurrentColumns}
                dataState={dataState}
                setDataState={setDataState}
                saveTableState={saveTableStateRef}
                selectionDisabledField={props.selectionDisabledField}
                hiddenColumnsUpdated={hiddenColumnsUpdated}
                externalColumnsState={columnsStateToApply}
                onStateChange={(json) => {
                    setGridStateToken((n) => n + 1);
                    setCurrentGridStateJson(json);
                }}
            />
        </Card>
    );
});

export default CardGrid;
